import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ModalTrigger from '../../shared/modal/ModalTrigger';
import { worstAreaOfPain } from '../../shared/util/enum';
import { Checkbox } from '../questions/checkbox/Checkbox';
import { CheckboxRadio } from '../questions/checkboxRadio/CheckboxRadio';
import { DialSlider } from '../questions/dialSlider/DialSlider';
import { MskBody } from '../questions/mskBody/MskBody';
import { MultipleNumberSelect } from '../questions/multipleNumberSelect/MultipleNumberSelect';
import { PainSlider } from '../questions/painSlider/PainSlider';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function PhysicalHealth1({
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onOpenModal,
  onSetValue,
}: AssessmentPageItem) {
  const { t } = useTranslation();
  const currentJointPainOptions = [
    { id: 'yes', title: t('PH001') },
    { id: 'no', title: t('PH002') },
  ];

  let sex;
  if (assessmentValues.tailor_recommendations === 'male_specific') {
    sex = 'male';
  } else if (assessmentValues.tailor_recommendations === 'female_specific') {
    sex = 'female';
  } else {
    sex = 'neutral';
  }

  const seenSpecialistOptions = [
    { id: 'yes', title: t('PH017') },
    { id: 'no', title: t('PH018') },
  ];

  const painTextValues = [t('PH019'), t('PH020'), t('PH021'), t('PH022'), t('PH023')];

  const painConstantOptions = [
    { id: 'constant', title: t('PH024') },
    { id: 'intermittent', title: t('PH025') },
  ];

  const timeOptions = [
    { id: 'years', trailingText: t('PH026'), limit: '99' },
    { id: 'months', trailingText: t('PH027'), limit: '12' },
    { id: 'weeks', trailingText: t('PH028'), limit: '52' },
  ];

  const longTermTreatmentOptions = [
    { id: 'yes', title: t('PH029') },
    { id: 'no', title: t('PH030') },
  ];

  const longTermConditionOptions = [
    { id: 'none', title: t('PH031') },
    { id: 'anaemia', title: t('PH032') },
    { id: 'asthma', title: t('PH033') },
    { id: 'copd', title: t('PH034') },
    { id: 'cancer', title: t('PH035') },
    { id: 'cardiovascular_disease', title: t('PH036') },
    { id: 'chronic_fatigue', title: t('PH037') },
    { id: 'chronic_pain', title: t('PH038') },
    { id: 'fibromyalgia', title: t('PH039') },
    { id: 'hay_fever', title: t('PH040') },
    { id: 'inflamatory_bowel_disease', title: t('PH041') },
    { id: 'ibs', title: t('PH042') },
    { id: 'kidney_disease', title: t('PH043') },
    { id: 'long_covid', title: t('PH044') },
    { id: 'multiple_sclerosis', title: t('PH045') },
    { id: 'osteoarthritis', title: t('PH046') },
    { id: 'osteoporosis', title: t('PH047') },
    { id: 'parkinsons_disease', title: t('PH048') },
    { id: 'rheumatoid_arthritis', title: t('PH049') },
    { id: 'type_1_diabetes', title: t('PH050') },
    { id: 'type_2_diabetes', title: t('PH051') },
    { id: 'other', title: t('PH052') },
  ];

  const coldFluOptions = [
    { id: 'yes', title: t('PH053') },
    { id: 'no', title: t('PH054') },
  ];

  const workingDayConditionOptions = [
    { id: 'none', title: t('PH055') },
    { id: 'tired_eyes', title: t('PH056') },
    { id: 'dry_eyes', title: t('PH057') },
    { id: 'headaches', title: t('PH058') },
    { id: 'migraines', title: t('PH059') },
    { id: 'blurred_vision', title: t('PH060') },
  ];

  const menstrualStatusOptions = [
    { id: 'periods', title: t('PH061') },
    { id: 'pregnant', title: t('PH062') },
    { id: 'perimenopausal', title: t('PH063') },
    { id: 'menopausal', title: t('PH064') },
    { id: 'postmenopausal', title: t('PH065') },
    { id: 'no_periods', title: t('PH066') },
    { id: 'other', title: t('PH067') },
  ];

  const trimesterOptions = [
    { id: 'trimester1', title: t('PH068') },
    { id: 'trimester2', title: t('PH069') },
    { id: 'trimester3', title: t('PH070') },
  ];

  const textValue = [t('PH071'), t('PH072')];

  useEffect(() => {
    if (
      assessmentValues.current_joint_muscle_pain !== 'yes' ||
      assessmentValues.current_joint_muscle_pain === undefined
    ) {
      onSetValue({ id: 'areas_of_pain', value: null });
      onSetValue({ id: 'worst_area_of_pain', value: null });
      onSetValue({ id: 'pain_constant', value: null });
      onSetValue({ id: 'pain', value: null });
      onSetValue({ id: 'duration_of_pain', value: null });
      onSetValue({ id: 'seen_specialist_pain', value: null });
    }
  }, [assessmentValues.current_joint_muscle_pain]);

  useEffect(() => {
    if (assessmentValues.areas_of_pain?.length > 1) {
      onSetValue({ id: 'worst_area_of_pain', value: null });
    }
  }, [assessmentValues.areas_of_pain]);

  useEffect(() => {
    if (
      assessmentValues.worst_area_of_pain === undefined ||
      assessmentValues.worst_area_of_pain === null
    ) {
      onSetValue({ id: 'pain_constant', value: null });
      onSetValue({ id: 'seen_specialist_pain', value: null });
    }
  }, [assessmentValues.worst_area_of_pain]);

  useEffect(() => {
    if (
      assessmentValues.long_term_conditions === undefined ||
      assessmentValues.long_term_conditions[0].id === 'none'
    ) {
      onSetValue({ id: 'long_term_treatment', value: null });
    }
  }, [assessmentValues.long_term_conditions]);

  useEffect(() => {
    if (assessmentValues.tailor_recommendations !== 'female_specific') {
      onSetValue({ id: 'menstrual_status', value: null });
      onSetValue({ id: 'trimester', value: null });
    }
  }, [assessmentValues.tailor_recommendations]);

  useEffect(() => {
    if (
      assessmentValues.menstrual_status !== 'pregnant' ||
      assessmentValues.menstrual_status === undefined
    ) {
      onSetValue({ id: 'trimester', value: null });
    }
  }, [assessmentValues.menstrual_status]);

  return (
    <div className="question_container" id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>

      <Radio
        questionid="current_joint_muscle_pain"
        width=""
        questionTitle={t('assessment.question.current_joint_muscle_pain')}
        options={currentJointPainOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      {assessmentValues.current_joint_muscle_pain === 'yes' && (
        <div>
          <fieldset data-input-type="question_block">
            <legend className="assessment_question_title">
              {t('assessment.question.areas_of_pain')}
            </legend>

            <ModalTrigger
              modalText={t('PH074')}
              onOpenModal={onOpenModal}
              question="areas"
              questionTitle={t('assessment.question.areas_of_pain')}
            />
            <MskBody
              questionid="areas_of_pain"
              sex={sex}
              onSetValue={onSetValue}
              areas_of_pain={assessmentValues.areas_of_pain}
            />
            {assessmentValues.areas_of_pain && assessmentValues.areas_of_pain.length > 1 ? (
              <CheckboxRadio
                questionid="worst_area_of_pain"
                width=""
                questionTitle={t('assessment.question.worst_area_of_pain')}
                options={worstAreaOfPain}
                includeOnly={assessmentValues.areas_of_pain}
                onSetValue={onSetValue}
                assessmentValues={assessmentValues}
              />
            ) : null}
          </fieldset>

          {assessmentValues.worst_area_of_pain ? (
            <Radio
              questionid="pain_constant"
              width=""
              questionTitle={
                t('PH080') + assessmentValues.worst_area_of_pain.id.replace('_', ' ') + t('PH081')
              }
              options={painConstantOptions}
              onSetValue={onSetValue}
              assessmentValues={assessmentValues}
              modalText={t('PH082')}
              onOpenModal={onOpenModal}
            />
          ) : null}
          <PainSlider
            questionid="pain"
            width=""
            textValues={painTextValues}
            questionTitle={t('assessment.question.pain')}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
          />
          <MultipleNumberSelect
            questionid="duration_of_pain"
            questionTitle={t('assessment.question.duration_of_pain')}
            options={timeOptions}
            width=""
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
            modalText={t('PH085')}
            onOpenModal={onOpenModal}
          />
          {assessmentValues.worst_area_of_pain ? (
            <Radio
              questionid="seen_specialist_pain"
              width=""
              questionTitle={
                t('PH086') + assessmentValues.worst_area_of_pain.id.replace('_', ' ') + t('PH087')
              }
              options={seenSpecialistOptions}
              onSetValue={onSetValue}
              assessmentValues={assessmentValues}
              modalText={t('PH088')}
              onOpenModal={onOpenModal}
              fadeIn="yes"
            />
          ) : null}
        </div>
      )}
      <hr />
      <Checkbox
        questionid="long_term_conditions"
        width="_wide"
        questionTitle={t('assessment.question.long_term_conditions')}
        questionTitleSuffix=""
        options={longTermConditionOptions}
        itemLimit="3"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        fadeIn="yes"
        modalText={t('PH091')}
        onOpenModal={onOpenModal}
      />
      {assessmentValues.long_term_conditions &&
      assessmentValues.long_term_conditions.filter((item) => item.id === 'none').length === 0 ? (
        <Radio
          questionid="long_term_treatment"
          width=""
          questionTitle={t('assessment.question.long_term_treatment')}
          options={longTermTreatmentOptions}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          fadeIn="yes"
          modalText={t('PH093')}
          onOpenModal={onOpenModal}
        />
      ) : null}
      <Radio
        questionid="frequent_colds_flu"
        width=""
        questionTitle={t('assessment.question.frequent_colds_flu')}
        options={coldFluOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('PH095')}
        onOpenModal={onOpenModal}
      />
      <Checkbox
        questionid="working_day_conditions"
        width=""
        questionTitle={t('assessment.question.working_day_conditions')}
        questionTitleSuffix={t('PH097')}
        options={workingDayConditionOptions}
        itemLimit="2"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        secondaryQ
      />
      {assessmentValues.tailor_recommendations === 'female_specific' && (
        <Radio
          questionid="menstrual_status"
          width=""
          questionTitle={t('assessment.question.menstrual_status')}
          options={menstrualStatusOptions}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          modalText={t('PH100')}
          onOpenModal={onOpenModal}
        />
      )}
      {assessmentValues.menstrual_status &&
        sex === 'female' &&
        assessmentValues.menstrual_status === 'pregnant' && (
          <Radio
            questionid="trimester"
            width=""
            questionTitle={t('assessment.question.trimester')}
            options={trimesterOptions}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
            fadeIn="yes"
          />
        )}
      <DialSlider
        questionid="overall_physical_health"
        width=""
        questionTitle={t('assessment.question.overall_physical_health')}
        textValue={textValue}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('PH103')}
        onOpenModal={onOpenModal}
      />
    </div>
  );
}
